/**
 * useSearchInput — universal 3-char-minimum, debounced search hook.
 *
 * Returns:
 *   q        — instantaneous input value (bind to <input value={q} onChange>)
 *   setQ     — setter for the input value
 *   activeQ  — debounced query value, "" until q.length >= MIN_CHARS,
 *              then becomes q after DEBOUNCE_MS of no typing. Use this in
 *              your API calls / filter logic.
 *   isReady  — true when activeQ should drive filtering (length >= MIN)
 *   hint     — short helper text to render under the input:
 *              ""                 — nothing typed
 *              "x more to search" — 1..(MIN-1) chars typed
 *              ""                 — searching (≥3)
 *   clear    — clears both q and activeQ
 *   MIN_CHARS / DEBOUNCE_MS — exposed for UI labels
 *
 * Usage:
 *   const { q, setQ, activeQ, hint } = useSearchInput();
 *   useEffect(() => { reload(activeQ); }, [activeQ]);
 *   <input value={q} onChange={(e)=>setQ(e.target.value)} />
 *   {hint && <span className="text-xs text-zinc-500">{hint}</span>}
 */
import { useEffect, useRef, useState } from "react";

export const MIN_CHARS = 3;
const DEBOUNCE_MS = 250;

export default function useSearchInput(initial = "", minChars = MIN_CHARS) {
    const [q, setQ] = useState(initial);
    const [activeQ, setActiveQ] = useState(
        initial.trim().length >= minChars ? initial.trim() : ""
    );
    const timerRef = useRef(null);

    useEffect(() => {
        if (timerRef.current) clearTimeout(timerRef.current);
        const trimmed = (q || "").trim();
        if (trimmed.length < minChars) {
            // Below threshold: clear active query immediately so filtering stops.
            if (activeQ !== "") setActiveQ("");
            return undefined;
        }
        timerRef.current = setTimeout(() => setActiveQ(trimmed), DEBOUNCE_MS);
        return () => clearTimeout(timerRef.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [q]);

    const trimmedLen = (q || "").trim().length;
    let hint = "";
    if (trimmedLen > 0 && trimmedLen < minChars) {
        const remaining = minChars - trimmedLen;
        hint = `Type ${remaining} more ${remaining === 1 ? "letter" : "letters"} to search…`;
    }

    const clear = () => {
        if (timerRef.current) clearTimeout(timerRef.current);
        setQ("");
        setActiveQ("");
    };

    return {
        q, setQ, activeQ, clear, hint,
        isReady: activeQ.length >= minChars,
        MIN_CHARS: minChars, DEBOUNCE_MS,
    };
}
